import { isAndroid } from 'react-device-detect';
import { logger } from './logger';

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;

// 환경 감지
const isReactNative = () => {
  return typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;
};

const isAndroidWebView = () => {
  return typeof window !== 'undefined' && window.Android !== undefined;
};

// 전역 이미지 캐시
const globalImageCache = new Map<string, string>();

/**
 * MIME 타입 추출 함수
 */
export const getMimeType = (imagePath: string): string => {
  const extension = imagePath.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    default:
      return "application/octet-stream";
  }
};

/**
 * 전역 이미지 캐시에서 이미지 가져오기
 */
export const getCachedImage = (imagePath: string | null): string | null => {
  if (!imagePath) return null;
  return globalImageCache.get(imagePath) || null;
};

/**
 * 전역 이미지 캐시에 이미지 저장
 */
export const setCachedImage = (imagePath: string, base64Data: string): void => {
  globalImageCache.set(imagePath, base64Data);
};

/**
 * Android 네이티브 이미지를 base64로 변환하여 캐시에 저장
 */
export const loadImageAsBase64 = (imagePath: string): Promise<string> => {
  return new Promise((resolve) => {
    // 이미 캐시에 있는 경우
    const cached = getCachedImage(imagePath);
    if (cached) {
      resolve(cached);
      return;
    }

    // 환경별 이미지 로드 (우선순위 높음) - 테블렛 최적화
    if (isAndroidWebView() && window.Android?.getImageAsBase64) {
      // Android WebView 환경 - 동기적 시도 (테블렛에서 더 빠름)
      try {
        const base64Image = window.Android.getImageAsBase64(imagePath);
        if (base64Image) {
          const mimeType = getMimeType(imagePath);
          const src = `data:${mimeType};base64,${base64Image}`;
          setCachedImage(imagePath, src);
          resolve(src);
          return;
        }
      } catch (error) {
        logger.warn(`Failed to load image as base64: ${imagePath}`, error);
      }
      
      // 동기 로드 실패 시 즉시 비동기 재시도 (지연 없음)
      try {
        const base64Image = window.Android?.getImageAsBase64(imagePath);
        if (base64Image) {
          const mimeType = getMimeType(imagePath);
          const src = `data:${mimeType};base64,${base64Image}`;
          setCachedImage(imagePath, src);
          resolve(src);
          return;
        }
      } catch (error) {
        logger.warn(`Failed to load image asynchronously: ${imagePath}`, error);
      }
      
      // 최종 fallback - 웹 URL (테블렛에서도 빠름)
      const webUrl = IMAGE_URL + `/${imagePath}`;
      setCachedImage(imagePath, webUrl);
      resolve(webUrl);
    } else if (isReactNative()) {
      // React Native 환경 - 웹 URL 사용
      const webUrl = IMAGE_URL + `/${imagePath}`;
      setCachedImage(imagePath, webUrl);
      resolve(webUrl);
    } else {
      // 웹/테블렛 환경에서는 웹 URL 반환하되, 캐시에 저장
      const webUrl = IMAGE_URL + `/${imagePath}`;
      setCachedImage(imagePath, webUrl);
      resolve(webUrl);
    }
  });
};

/**
 * 이미지 소스 결정 (캐시 우선, 없으면 로드)
 */
export const getImageSrc = async (imagePath: string | null): Promise<string> => {
  if (!imagePath) return "/assets/images/no_image.png";
  
  // 캐시에서 확인
  const cached = getCachedImage(imagePath);
  if (cached) {
    return cached;
  }
  
  // 캐시에 없으면 로드
  return await loadImageAsBase64(imagePath);
};

/**
 * 여러 이미지를 동시에 로드 (테블렛 최적화)
 */
export const loadMultipleImages = async (imagePaths: string[]): Promise<void> => {
  // 캐시되지 않은 이미지만 필터링
  const uncachedPaths = imagePaths.filter(path => !getCachedImage(path));
  
  if (uncachedPaths.length === 0) {
    return; // 모든 이미지가 캐시되어 있음
  }
  
  // 병렬 로드 (테블렛에서 더 빠름)
  const loadPromises = uncachedPaths.map(async (path) => {
    try {
      await loadImageAsBase64(path);
    } catch (error) {
      logger.warn(`Failed to load image: ${path}`, error);
    }
  });
  
  await Promise.all(loadPromises);
};

/**
 * 여러 이미지를 프리로드 (기존 API 호환성)
 */
export const preloadImages = async (imagePaths: string[]): Promise<void> => {
  return loadMultipleImages(imagePaths);
};

/**
 * 특정 이미지가 캐시되어 있는지 확인
 */
export const isImageCached = (imagePath: string): boolean => {
  return globalImageCache.has(imagePath);
};

/**
 * 캐시 상태 확인
 */
export const getCacheStats = () => {
  return {
    size: globalImageCache.size,
    keys: Array.from(globalImageCache.keys())
  };
}; 